<script setup lang="ts">
import { isString } from "lodash-es";
import type { Ref, WatchStopHandle } from "vue";
import { computed, ref, useTemplateRef, watch } from "vue";

import { stickyNoteAttributeChipClicked } from "@/analytics/events/sticky";
import { trackEvent } from "@/analytics/track";
import { cardKey, cardMetaKey } from "@/components/StickyNote/injectKeys";
import { boardKey } from "@/components/board/injectKeys";
import DropdownMenu from "@/components/ui/DropdownMenu/DropdownMenu.vue";
import type { TooltipProp } from "@/composables/useTooltip";
import { useTooltip } from "@/composables/useTooltip";
import { useBoardStore } from "@/store/board";
import { cardColor } from "@/store/card";
import { useDraggingStore } from "@/store/dragging";
import { useZoomStore } from "@/store/zoom";
import { isDarkColor } from "@/utils/color";
import type { UnknownRecord } from "@/utils/type";
import { injectStrict } from "@/utils/vue";

interface Props {
  as?: "button" | "a";
  text?: string;
  textPlaceholder?: string;
  tooltip?: TooltipProp;
  tooltipWithContext?: boolean;
  dropdownProps?: UnknownRecord;
  shrink?: boolean; // flex-shrink: 0/1
  active?: boolean;
  readonly?: boolean;
  name?: string; // used for event tracking analytics
  triggerAriaLabel?: string;
  highlightOnChange?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  as: "button",
  text: undefined,
  textPlaceholder: undefined,
  tooltip: undefined,
  shrink: false,
  dropdownProps: () => ({ stayOpenOnContentClick: true }),
  name: undefined,
  triggerAriaLabel: undefined,
});

const triggerRef = useTemplateRef<HTMLElement>("trigger");
const dropdownRef = useTemplateRef("dropdown");

const highlighted = ref(false);

const tooltip = computed(() => props.tooltip);
const tippyRef = useTooltip({
  el: triggerRef,
  tooltip,
  aria: { content: null },
  keepContext: props.tooltipWithContext,
});

const triggerAriaLabel = computed(() => {
  const tooltip = isString(props.tooltip) ? `${props.tooltip}: ` : "";
  const text = props.text || props.textPlaceholder || "";
  return tooltip + text;
});

const card = injectStrict(cardKey);
const board = injectStrict(boardKey);
const cardMeta = injectStrict(cardMetaKey);
const isCardActive = computed(() => useBoardStore().activeCardId === card.id);
const theme = computed(() => (isDarkColor(cardColor(card, board.value)) ? "dark" : "light"));

defineOptions({ inheritAttrs: false });

const closeDropdown = () => {
  dropdownRef.value?.close();
};

let zoomingWatcher: WatchStopHandle | undefined;
let boardChangeWatcher: WatchStopHandle | undefined;

const handleOpen = () => {
  if (props.name) {
    const cardType = card.type.functionality;
    trackEvent(stickyNoteAttributeChipClicked(props.name, cardType, isCardActive.value));
  }

  zoomingWatcher = watch(() => useZoomStore().zooming, closeDropdown);
  boardChangeWatcher = watch(() => useBoardStore().board?.id, closeDropdown, { once: true });
};
const handleClose = () => {
  zoomingWatcher?.();
  boardChangeWatcher?.();
  dragWatcher.value?.();
  dragWatcher.value = null;
  tippyRef?.enable();
};

const dragWatcher: Ref<WatchStopHandle | null> = ref(null);
const stickyWasDragged = ref(false);

const handleDropdownPointerDown = () => {
  tippyRef?.disable();

  dragWatcher.value = watch(
    () => useDraggingStore().dragging[card.id],
    (isDragging) => {
      if (!isDragging) return;
      stickyWasDragged.value = true;
      closeDropdown();
    },
  );
};

const handleDropdownClick = (event: MouseEvent) => {
  dragWatcher.value?.();
  tippyRef?.enable();

  if (stickyWasDragged.value) {
    stickyWasDragged.value = false;
    event.preventDefault(); // Needed to prevent the alm-link from opening during a drag
    return;
  }
};

/**
 * If highlightOnChange is set, show a light animation whenever the text of the chip
 * is updated (eg. when the link's chip count changes).
 */
watch(
  () => props.highlightOnChange && props.text,
  (newText) => {
    if (!newText) return;

    highlighted.value = true;

    // Return to normal styling after 1 sec
    setTimeout(() => {
      highlighted.value = false;
    }, 1_000);
  },
);
</script>

<template>
  <DropdownMenu
    ref="dropdown"
    stay-open-on-content-click
    :disabled="stickyWasDragged"
    :readonly="readonly || cardMeta.isReadonly"
    :class="{ 'no-flex-shrink': !shrink }"
    max-height="md"
    width="md"
    v-bind="dropdownProps"
    @open="handleOpen"
    @close="handleClose"
    @click.stop="handleDropdownClick"
    @pointerdown="handleDropdownPointerDown"
  >
    <template #trigger="{ isOpen, contentId }">
      <component
        :is="as"
        ref="trigger"
        v-bind="$attrs"
        data-ignore-click
        data-dropdown-trigger
        :aria-label="props.triggerAriaLabel ?? triggerAriaLabel"
        :aria-expanded="isOpen"
        :aria-controls="isOpen ? contentId : undefined"
        aria-haspopup="listbox"
        :class="[
          'attribute-chip',
          theme,
          {
            active: text || active,
            rounded: !text && !textPlaceholder,
            readonly: readonly || cardMeta.isReadonly,
            highlighted,
          },
        ]"
        :tabindex="isCardActive ? 0 : -1"
      >
        <span v-if="$slots.icon" class="icon">
          <slot name="icon" />
        </span>
        <span v-if="text || textPlaceholder" class="text">
          {{ text || textPlaceholder }}
        </span>
      </component>
    </template>

    <div
      v-focus-trap="{
        fallbackFocus: triggerRef,
        returnFocusOnDeactivate: cardMeta.wasActivatedByKeyboard,
      }"
      data-ignore-click
      class="dropdown-wrapper"
    >
      <slot />
    </div>
  </DropdownMenu>
</template>

<style scoped lang="scss">
@use "@/styles/variables/a11y" as colors-a11y;
@use "@/styles/variables/colors";
@use "@/styles/mixins/utils";
@use "@/styles/mixins/typography";

.dropdown-wrapper {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

.no-flex-shrink {
  flex-shrink: 0;
}

.tooltip {
  display: inline-block;
  height: 100%;
}

.attribute-chip {
  display: inline-flex;
  align-items: center;
  padding: 3px 4px;
  height: 20px;
  border: 1px dashed currentcolor;
  user-select: none;
  border-radius: 4px;
  max-width: 100%;
  min-width: 0;

  @include typography.small;

  // Base styles to complement .highlighted
  outline: colors.$border-transparent;

  // Using longhand transition properties instead of shorthand to avoid html2canvas parsing errors
  transition-property: outline, background-color;
  transition-duration: 100ms;

  &:not(.readonly) {
    cursor: pointer;
  }

  &.light {
    color: colors-a11y.$outline-black;
  }

  &.dark {
    color: colors-a11y.$outline-white;
  }

  > * + * {
    margin-left: 4px;
  }

  &.rounded {
    border-radius: 50%;
    aspect-ratio: 1/1;
    justify-content: center;
  }

  // To highlight the chip, show a faint outline and darken the background
  &.highlighted {
    outline: 2px solid colors.$palette-black-alpha-10 !important;
    background-color: colors.$palette-black-alpha-20 !important;
  }

  .text {
    @include utils.ellipsis;
  }

  .icon {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
    height: 14px;
    width: 14px;
  }

  &:hover:not(.active, :active, .readonly) {
    &.light {
      color: colors.$palette-black-alpha-60;
    }

    &.dark {
      color: colors.$palette-white-alpha-80;
    }
  }

  &.active {
    padding: 3px 4px;
    border: none;

    &.light {
      color: colors.$text;
      background-color: colors.$palette-black-alpha-10;

      &:hover:not(.readonly) {
        background-color: colors.$palette-black-alpha-20;
      }

      &:active:not(.readonly) {
        background-color: colors.$palette-black-alpha-20;
      }
    }

    &.dark {
      color: colors.$palette-brand-10;
      background-color: colors.$palette-white-alpha-10;

      &:hover:not(.readonly) {
        background-color: colors.$palette-white-alpha-20;
      }

      &:active:not(.readonly) {
        background-color: colors.$palette-white-alpha-20;
      }
    }
  }

  .wrapper {
    flex-grow: 1; /* Takes all available space */
    display: flex;
    flex-direction: column;
    min-height: 0; /* Overcomes the minimum height issue in flex children */
  }
}
</style>
